import { Col, DatePicker, Form, Input, Select } from "antd";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CHALLAN_LIST,
  COLOR_LIST_ENDPOINT,
  GET_BUYER_ENDPOINT,
  GET_BUYING_OFFICE_ENDPOINT,
  GET_ITEM_CATEGORIES,
  GET_SUPPLIER_ENDPOINT,
  INVOICES_LIST_ENDPOINT,
  SALES_CONTRACT_LIST,
  STYLE_MERCHANDISING_LIST,
  SUBSTYLE_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import { incomingMovementTypes, outgoingMovementTypes } from "../../../common/helper/helper";
import { isArrayAndHasValue } from "../../../utils/functions";
import "./FabricStoreEntryFilterFields.css";

const movementFilterOptions = [...outgoingMovementTypes, ...incomingMovementTypes].map((m) => ({
  label: m.label,
  value: m.value,
}));

const SEARCH_DEBOUNCE_MS = 400;

/** Dedupe initial option loads when the filter panel is opened/closed/remounted */
let fabricStoreInitialCache = null;
let fabricStoreInitialPromise = null;

const extractList = (res) => {
  const list = res?.data?.data?.data ?? res?.data?.data;
  return Array.isArray(list) ? list : [];
};

async function loadAllInitialOptionLists() {
  if (fabricStoreInitialCache) {
    return fabricStoreInitialCache;
  }
  if (fabricStoreInitialPromise) {
    return fabricStoreInitialPromise;
  }

  const fetchPromise = (async () => {
    const [
      buyingRes,
      salesRes,
      styleRes,
      subRes,
      colorRes,
      buyerRes,
      catRes,
      supRes,
      challanRes,
      invRes,
    ] = await Promise.all([
      getData(GET_BUYING_OFFICE_ENDPOINT, false, { per_page: 100, name: "" }),
      getData(SALES_CONTRACT_LIST, false, { per_page: 100, reference_no: "" }),
      getData(STYLE_MERCHANDISING_LIST, false, { per_page: 100, style_no: "" }),
      getData(SUBSTYLE_LIST, false, {
        per_page: 100,
        page: 1,
        sub_style_no: "",
        style_id: null,
      }),
      getData(COLOR_LIST_ENDPOINT, false, { per_page: 100, name: "" }),
      getData(GET_BUYER_ENDPOINT, false, { per_page: 100, name: "" }),
      getData(GET_ITEM_CATEGORIES, false, { per_page: 100, name: "" }),
      getData(GET_SUPPLIER_ENDPOINT, false, { per_page: 100, name: "" }),
      getData(CHALLAN_LIST, false, { per_page: 100, name: "" }),
      getData(INVOICES_LIST_ENDPOINT, false, { per_page: 100, page: 1 }),
    ]);

    const invoicePayload = invRes?.data?.data?.data ?? invRes?.data?.data ?? invRes?.data;
    const invoices = Array.isArray(invoicePayload) ? invoicePayload : [];

    fabricStoreInitialCache = {
      buyingOffice: extractList(buyingRes),
      salesContracts: extractList(salesRes),
      styles: extractList(styleRes),
      subStyles: extractList(subRes),
      colors: extractList(colorRes),
      buyers: extractList(buyerRes),
      itemCategories: extractList(catRes),
      suppliers: extractList(supRes),
      challans: extractList(challanRes),
      invoices,
    };
    return fabricStoreInitialCache;
  })();

  fabricStoreInitialPromise = fetchPromise
    .catch((err) => {
      fabricStoreInitialPromise = null;
      fabricStoreInitialCache = null;
      throw err;
    })
    .finally(() => {
      fabricStoreInitialPromise = null;
    });

  return fetchPromise;
}

const FabricStoreEntryFilterFields = () => {
  const { Option } = Select;
  const form = Form.useFormInstance();
  const mounted = useRef(true);

  const [buyingOffice, setBuyingOffice] = useState([]);
  const [salesContractList, setSalesContractList] = useState([]);
  const [styleList, setStyleList] = useState([]);
  const [subStyleList, setSubStyleList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [buyerList, setBuyerList] = useState([]);
  const [itemCategoryList, setItemCategoryList] = useState([]);
  const [supplierList, setSupplierList] = useState([]);
  const [challanList, setChallanList] = useState([]);
  const [invoiceList, setInvoiceList] = useState([]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const getBuyingOfficeList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, name: filterValues?.name || "" };
    const res = await getData(GET_BUYING_OFFICE_ENDPOINT, false, bodyData);
    if (res && mounted.current) {
      setBuyingOffice(res?.data?.data?.data || []);
    }
  }, []);

  const getSalesContractList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, reference_no: filterValues?.reference_no || "" };
    const res = await getData(SALES_CONTRACT_LIST, false, bodyData);
    if (res && mounted.current) {
      setSalesContractList(res?.data?.data?.data || []);
    }
  }, []);

  const getStyleList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, style_no: filterValues?.style_no || "" };
    const res = await getData(STYLE_MERCHANDISING_LIST, false, bodyData);
    if (res?.data?.code === 200 && mounted.current) {
      setStyleList(res?.data?.data?.data || []);
    }
  }, []);

  const getSubStyles = useCallback(async (filterValues = {}) => {
    const bodyData = {
      per_page: 100,
      page: filterValues?.page || 1,
      sub_style_no: filterValues?.sub_style_no || "",
      style_id: filterValues?.style_id ?? null,
    };
    const res = await getData(SUBSTYLE_LIST, false, bodyData);
    if (res?.data?.code === 200 && mounted.current) {
      setSubStyleList(res?.data?.data?.data || []);
    }
  }, []);

  const getColorList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, name: filterValues?.name || "" };
    const res = await getData(COLOR_LIST_ENDPOINT, false, bodyData);
    if (res?.data?.code === 200 && mounted.current) {
      setColorList(res?.data?.data?.data || []);
    }
  }, []);

  const getBuyerList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, name: filterValues?.name || "" };
    const res = await getData(GET_BUYER_ENDPOINT, false, bodyData);
    if (res?.data?.code === 200 && mounted.current) {
      setBuyerList(res?.data?.data?.data || []);
    }
  }, []);

  const getItemCategoryList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, name: filterValues?.name || "" };
    const res = await getData(GET_ITEM_CATEGORIES, false, bodyData);
    if (res?.data?.code === 200 && mounted.current) {
      setItemCategoryList(res?.data?.data?.data || []);
    }
  }, []);

  const getSupplierList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, name: filterValues?.name || "" };
    const res = await getData(GET_SUPPLIER_ENDPOINT, false, bodyData);
    if (res?.data?.code === 200 && mounted.current) {
      setSupplierList(res?.data?.data?.data || []);
    }
  }, []);

  const getChallanList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, name: filterValues?.name || "" };
    const res = await getData(CHALLAN_LIST, false, bodyData);
    if (res?.data?.code === 200 && mounted.current) {
      setChallanList(res?.data?.data?.data || []);
    }
  }, []);

  const getInvoiceList = useCallback(async (filterValues = {}) => {
    const bodyData = { per_page: 100, page: 1, ...filterValues };
    const res = await getData(INVOICES_LIST_ENDPOINT, false, bodyData);
    if (!mounted.current) return;
    if (res?.status === 200 || res?.data?.code === 200) {
      const list = res?.data?.data?.data || res?.data?.data || [];
      setInvoiceList(Array.isArray(list) ? list : []);
    }
  }, []);

  const debouncedBuyingOfficeSearch = useMemo(
    () => debounce((value) => getBuyingOfficeList({ name: value }), SEARCH_DEBOUNCE_MS),
    [getBuyingOfficeList],
  );
  const debouncedSalesContractSearch = useMemo(
    () => debounce((value) => getSalesContractList({ reference_no: value }), SEARCH_DEBOUNCE_MS),
    [getSalesContractList],
  );
  const debouncedStyleSearch = useMemo(
    () => debounce((value) => getStyleList({ style_no: value }), SEARCH_DEBOUNCE_MS),
    [getStyleList],
  );
  const debouncedSubStyleSearch = useMemo(
    () =>
      debounce((value, styleId) => {
        getSubStyles({ sub_style_no: value, style_id: styleId || null });
      }, SEARCH_DEBOUNCE_MS),
    [getSubStyles],
  );
  const debouncedColorSearch = useMemo(
    () => debounce((value) => getColorList({ name: value }), SEARCH_DEBOUNCE_MS),
    [getColorList],
  );
  const debouncedBuyerSearch = useMemo(
    () => debounce((value) => getBuyerList({ name: value }), SEARCH_DEBOUNCE_MS),
    [getBuyerList],
  );
  const debouncedItemCategorySearch = useMemo(
    () => debounce((value) => getItemCategoryList({ name: value }), SEARCH_DEBOUNCE_MS),
    [getItemCategoryList],
  );
  const debouncedSupplierSearch = useMemo(
    () => debounce((value) => getSupplierList({ name: value }), SEARCH_DEBOUNCE_MS),
    [getSupplierList],
  );
  const debouncedChallanSearch = useMemo(
    () => debounce((value) => getChallanList({ name: value }), SEARCH_DEBOUNCE_MS),
    [getChallanList],
  );
  const debouncedInvoiceSearch = useMemo(
    () =>
      debounce((value) => getInvoiceList({ invoice_number: value, search: value }), SEARCH_DEBOUNCE_MS),
    [getInvoiceList],
  );

  useEffect(() => {
    return () => {
      debouncedBuyingOfficeSearch.cancel();
      debouncedSalesContractSearch.cancel();
      debouncedStyleSearch.cancel();
      debouncedSubStyleSearch.cancel();
      debouncedColorSearch.cancel();
      debouncedBuyerSearch.cancel();
      debouncedItemCategorySearch.cancel();
      debouncedSupplierSearch.cancel();
      debouncedChallanSearch.cancel();
      debouncedInvoiceSearch.cancel();
    };
  }, [
    debouncedBuyingOfficeSearch,
    debouncedSalesContractSearch,
    debouncedStyleSearch,
    debouncedSubStyleSearch,
    debouncedColorSearch,
    debouncedBuyerSearch,
    debouncedItemCategorySearch,
    debouncedSupplierSearch,
    debouncedChallanSearch,
    debouncedInvoiceSearch,
  ]);

  useEffect(() => {
    let cancelled = false;
    loadAllInitialOptionLists()
      .then((data) => {
        if (cancelled || !mounted.current) return;
        setBuyingOffice(data.buyingOffice);
        setSalesContractList(data.salesContracts);
        setStyleList(data.styles);
        setSubStyleList(data.subStyles);
        setColorList(data.colors);
        setBuyerList(data.buyers);
        setItemCategoryList(data.itemCategories);
        setSupplierList(data.suppliers);
        setChallanList(data.challans);
        setInvoiceList(data.invoices);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Col span={4}>
        <Form.Item label="Entry date" name="entry_date">
          <DatePicker
            className="fabric-store-entry-filter__date"
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            size="small"
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Movement type" name="movement_type">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            options={movementFilterOptions}
            placeholder="Movement"
            size="small"
          />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Buying office" name="buying_office_id">
          <Select
            showSearch
            placeholder="Buying office"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) => debouncedBuyingOfficeSearch(value)}
            onClear={() => {
              debouncedBuyingOfficeSearch.cancel();
              getBuyingOfficeList();
            }}
          >
            {buyingOffice.map((office) => (
              <Option key={office.id} value={office.id}>
                {office.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Sales contract" name="sales_contract_id">
          <Select
            showSearch
            placeholder="Sales contract"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) => debouncedSalesContractSearch(value)}
            onClear={() => {
              debouncedSalesContractSearch.cancel();
              getSalesContractList();
            }}
          >
            {salesContractList.map((contract) => (
              <Option key={contract.id} value={contract.id}>
                {contract.reference_no}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Style" name="style_id">
          <Select
            showSearch
            placeholder="Style"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) => debouncedStyleSearch(value)}
            onClear={() => {
              debouncedStyleSearch.cancel();
              getStyleList();
            }}
          >
            {styleList.map((style) => (
              <Option key={style.id} value={style.id}>
                {style.style_no}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Sub style" name="sub_style_id">
          <Select
            showSearch
            placeholder="Sub style"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) =>
              debouncedSubStyleSearch(value, form.getFieldValue("style_id") || null)
            }
            onClear={() => {
              debouncedSubStyleSearch.cancel();
              getSubStyles({ style_id: form.getFieldValue("style_id") || null });
            }}
          >
            {isArrayAndHasValue(subStyleList) &&
              subStyleList.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.sub_style_no || item.name || item.id}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Color" name="color_id">
          <Select
            showSearch
            placeholder="Color"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) => debouncedColorSearch(value)}
            onClear={() => {
              debouncedColorSearch.cancel();
              getColorList();
            }}
          >
            {colorList.map((c) => (
              <Option key={c.id} value={c.id}>
                {c.name || c.color_name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Buyer" name="buyer_id">
          <Select
            showSearch
            placeholder="Buyer"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) => debouncedBuyerSearch(value)}
            onClear={() => {
              debouncedBuyerSearch.cancel();
              getBuyerList();
            }}
          >
            {buyerList.map((buyer) => (
              <Option key={buyer.id} value={buyer.id}>
                {buyer.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Item category" name="item_category_id">
          <Select
            showSearch
            placeholder="Item category"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) => debouncedItemCategorySearch(value)}
            onClear={() => {
              debouncedItemCategorySearch.cancel();
              getItemCategoryList();
            }}
          >
            {isArrayAndHasValue(itemCategoryList) &&
              itemCategoryList.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Supplier" name="supplier_id">
          <Select
            showSearch
            placeholder="Supplier"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) => debouncedSupplierSearch(value)}
            onClear={() => {
              debouncedSupplierSearch.cancel();
              getSupplierList();
            }}
          >
            {supplierList.map((s) => (
              <Option key={s.id} value={s.id}>
                {s.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Challan" name="challan_id">
          <Select
            showSearch
            placeholder="Challan"
            optionFilterProp="children"
            size="small"
            allowClear
            onSearch={(value) => debouncedChallanSearch(value)}
            onClear={() => {
              debouncedChallanSearch.cancel();
              getChallanList();
            }}
          >
            {isArrayAndHasValue(challanList) &&
              challanList.map((ch) => (
                <Option key={ch.id} value={ch.id}>
                  {ch.challan_no || ch.name || ch.id}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Invoice" name="invoice_id">
          <Select
            showSearch
            placeholder="Invoice"
            optionFilterProp="children"
            size="small"
            allowClear
            filterOption={false}
            onSearch={(value) => debouncedInvoiceSearch(value)}
            onClear={() => {
              debouncedInvoiceSearch.cancel();
              getInvoiceList();
            }}
          >
            {invoiceList.map((inv) => (
              <Option key={inv.id} value={inv.id}>
                {inv.invoice_number || inv.invoice_no || inv.number || inv.id}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Reference no." name="reference_no" className="fabric-store-entry-filter-text-item">
          <Input
            className="fabric-store-entry-filter__text-input"
            placeholder="Reference"
            size="small"
            allowClear
          />
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Requisition no." name="requisition_no" className="fabric-store-entry-filter-text-item">
          <Input
            className="fabric-store-entry-filter__text-input"
            placeholder="Requisition"
            size="small"
            allowClear
          />
        </Form.Item>
      </Col>
    </>
  );
};

export default FabricStoreEntryFilterFields;
